import { useLocale as __useLocale } from "@/context/LocaleContext";
import { isOutOfStock, isPricePending } from "@/lib/inventory";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/api";
import { FALLBACK_IMG } from "@/lib/images";
import { EASE } from "@/components/motion";
import { Heart, Plus } from "lucide-react";

export function ProductCard({ product, index = 0, arch = false }) {
  const { t: __t } = __useLocale();
  const { addToCart, toggleFavorite, favorites } = useStore();
  const fav = favorites.includes(product.slug);
  const outOfStock = isOutOfStock(product);
  const pricePending = isPricePending(product);
  const hasChoices = (product.variants?.length || 0) > 1;

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: EASE }}
      className="group relative"
    >
      <Link to={`/urun/${product.slug}`} className="block">
        <div className={`relative overflow-hidden border border-shule-line bg-shule-cream ${arch ? "rounded-t-[100px] rounded-b-3xl" : "rounded-3xl"}`}>
          <div className="aspect-[3/4]">
            {product.images?.length ? <img
              src={product.images?.[0] || FALLBACK_IMG}
              alt={__t(product.name)}
              loading="lazy" decoding="async"
              onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            /> : <div className="h-full flex items-center justify-center px-4 text-center text-sm text-shule-soft">{__t("Görsel hazırlanıyor")}</div>}
          </div>
          {product.isNew && (
            <span className="absolute top-3 left-3 badge-tag !bg-shule-goldlight/90">{__t("Yeni")}</span>
          )}
          {!product.isNew && product.isBestseller && (
            <span className="absolute top-3 left-3 badge-tag !bg-shule-goldlight/90">{__t("Çok Satan")}</span>
          )}
          {!product.isNew && !product.isBestseller && product.compare_at && (
            <span className="absolute top-3 left-3 badge-tag !bg-shule-goldlight/90">{__t("Ayrıcalıklı")}</span>
          )}
          {product.is_demo && (
            <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-wider text-shule-paper/90 bg-shule-coffee/40 backdrop-blur-sm rounded-full px-2.5 py-1">{__t("Demo")}</span>
          )}
        </div>
      </Link>
      <button
        data-testid={`favorite-toggle-${product.slug}`}
        onClick={() => toggleFavorite(product.slug)}
        aria-label={__t(fav ? "Favorilerden çıkar" : "Favorilere ekle")} aria-pressed={fav}
        className="absolute top-3 right-3 w-11 h-11 rounded-full bg-shule-paper/90 backdrop-blur border border-shule-line flex items-center justify-center transition-colors hover:border-shule-gold"
      >
        <Heart className={`w-4 h-4 transition-colors ${fav ? "fill-shule-gold text-shule-gold" : "text-shule-mocha"}`} />
      </button>
      <div className="pt-3 px-1 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/urun/${product.slug}`}>
            <h3 className="font-serif text-lg leading-tight text-shule-coffee break-words">{__t(product.name)}</h3>
          </Link>
          <p className="text-sm mt-1">
            <span className="font-bold">{pricePending ? __t("Fiyat yakında") : formatPrice(product.price)}</span>
            {product.compare_at && (
              <span className="ml-2 text-shule-soft line-through text-xs">{formatPrice(product.compare_at)}</span>
            )}
          </p>
        </div>
        {pricePending ? null : outOfStock ? (
          <span className="shrink-0 min-h-[44px] px-3 rounded-full border border-shule-line flex items-center text-xs font-semibold text-shule-soft">{__t("Stokta Yok")}</span>
        ) : hasChoices ? (
          <Link
            to={`/urun/${product.slug}`}
            data-testid={`choose-variant-${product.slug}`}
            className="shrink-0 min-h-[44px] px-3 rounded-full border border-shule-lineaccent flex items-center text-xs font-semibold hover:border-shule-gold"
            aria-label={__t(`${product.name} seçeneklerini gör`)}
          >{__t("Seçenekler")}</Link>
        ) : (
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={() => addToCart(product, product.variants?.[0])}
            aria-label={__t(`${product.name} sepete ekle`)}
            className="shrink-0 w-11 h-11 rounded-full border border-shule-lineaccent flex items-center justify-center transition-all duration-300 hover:bg-shule-coffee hover:text-shule-paper hover:border-shule-coffee"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.article>
  );
}
